import React, { useState, cloneElement, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { OnWhen } from '@cvent/carina/components/OnWhen';
import { Col } from '@cvent/carina/components/Col';
import { Row } from '@cvent/carina/components/Row';
import { LoadingSpinner } from '@cvent/carina/components/LoadingSpinner';
import ErrorState from '@components/shared/ErrorState';
import { injectTestId } from '@cvent/nucleus-test-automation';
import useLayoutEffect from '@hooks/useLayoutEffect';
import useBreakpoints from '@hooks/useBreakpoints';
import PermissionError from '@components/shared/permissionError/PermissionError';
import useTheme from '@hooks/useTheme';
import Spacer from './Spacer';

export const MAIN_ELEMENT_ID = 'content';

const useStyles = ({
  maxContentWidth,
  disableContentMaxWidth,
  navHeight,
  stickyHeader,
  hideHeader
}) => {
  const { depth } = useTheme();

  return {
    wrapper: {
      display: 'flex',
      flexFlow: 'column nowrap',
      minHeight: '100vh',
      'div[data-layout="col"]': {
        transition: '0.3s ease all'
      },
      maxWidth: '100%'
    },
    contentWrapper: {
      alignItems: 'stretch',
      display: 'flex',
      flex: 1,
      minHeight: '100%',
      width: '100%'
    },
    stickyContainer: {
      position: stickyHeader ? 'sticky' : 'relative',
      top: 0,
      zIndex: depth.zIndex['800'],
      display: hideHeader ? 'none' : ''
    },
    main: {
      alignSelf: 'stretch',
      flexFlow: 'column',
      flex: 12,
      width: disableContentMaxWidth ? 'none' : maxContentWidth,
      minHeight: '100%',
      paddingTop: navHeight,
      marginTop: -navHeight
    },
    footer: {
      display: 'flex',
      flex: 0
    }
  };
};

const panelSizes = panelViewSizes => {
  const sizes = {};
  panelViewSizes.forEach(size => {
    sizes[size] = true;
  });
  return sizes;
};

/**
 * @deprecated Use AppPageLayout instead
 */
const PageLayout = ({
  header,
  coverImage,
  content,
  sidebar,
  footer,
  loading,
  error,
  permissionError,
  maxContentWidth = 1900,
  disableContentMaxWidth = false,
  testID,
  errorFields,
  contentHeaderContent,
  panelContent,
  engagementRef,
  panelViewSizes = ['xl', 'l', 'm', 's', 'defaultSize'],
  removeContentPadding = false,
  showContentHeader = false,
  showFooterSpacer = true,
  addSpacer = true, // DEPRECATED
  stickyHeader = true,
  hideHeader = false
}) => {
  const { isMobileOrTablet, heightWindow } = useBreakpoints();
  const [navHeight, setNavHeight] = useState(null);
  const styles = useStyles({
    maxContentWidth,
    disableContentMaxWidth,
    navHeight,
    stickyHeader,
    hideHeader
  });
  const footerRef = useRef(null);
  const mainContentRef = useRef(null);

  const [contentHeaderHeight, setContentHeaderHeight] = useState(null);
  const [contentHeight, setContentHeight] = useState(null);
  const [mainContentHeight, setMainContentHeight] = useState(null);
  const [footerHeight, setFooterHeight] = useState(null);
  const [footerMarginBottom, setFooterMarginBottom] = useState(0);

  const [navHeaderVisible, setNavHeaderVisible] = useState(true);

  const contentHeightRef = useRef(null);

  const latestContentHeight = contentHeightRef?.current?.clientHeight;
  const latestMainContentHeight = mainContentRef?.current?.clientHeight;

  useEffect(() => {
    const totalHeaderHeight = navHeight;

    const setFooterHeights = () => {
      const footerRec = footerRef?.current?.getBoundingClientRect();
      setFooterHeight(
        Math.max(
          heightWindow - (footerRec?.top || 0) + (showFooterSpacer ? 16 : 0),
          0
        )
      );
    };

    const handleScroll = () => {
      if (window.pageYOffset <= totalHeaderHeight && !navHeaderVisible) {
        setNavHeaderVisible(true);
      } else if (window.pageYOffset >= totalHeaderHeight && navHeaderVisible) {
        setNavHeaderVisible(false);
      }
      setFooterHeights();
    };

    setFooterHeights();
    document.addEventListener('scroll', handleScroll);
    setContentHeight(latestContentHeight);
    setMainContentHeight(latestMainContentHeight);

    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, [
    navHeight,
    navHeaderVisible,
    latestContentHeight,
    latestMainContentHeight,
    heightWindow,
    showFooterSpacer
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (
      isMobileOrTablet &&
      footerMarginBottom !== (engagementRef?.current?.clientHeight || 0)
    ) {
      setFooterMarginBottom(engagementRef?.current?.clientHeight || 0);
    }
  });

  let contentEl;
  if (loading && !error) {
    contentEl = (
      <LoadingSpinner {...injectTestId('page-loading-spinner')} size="l" />
    );
  } else if (permissionError) {
    contentEl = <PermissionError />;
  } else if (error) {
    contentEl = <ErrorState errorFields={errorFields} />;
  } else {
    let contentElement = content;

    if (typeof content === 'function') {
      const element = content({
        navHeight,
        contentHeight,
        navHeaderVisible,
        contentHeaderHeight,
        showContentHeader
      });
      if (React.isValidElement(element)) {
        contentElement = element;
      } else {
        contentElement = null;
      }
    }

    contentEl = (
      <>
        <Row>
          <Col
            width="fill"
            l={sidebar != null ? { width: 2 / 3 } : undefined}
            padding={removeContentPadding ? 0 : 8}
          >
            {contentElement}
          </Col>
          {sidebar != null && !isMobileOrTablet ? (
            <Col
              padding={removeContentPadding ? 0 : { start: 16 }}
              width={1 / 3}
            >
              {sidebar}
            </Col>
          ) : null}
        </Row>
        {sidebar != null && isMobileOrTablet ? (
          <Row>
            <Col>{sidebar}</Col>
          </Row>
        ) : null}
      </>
    );
  }

  const fullPageLayoutContent = (
    <>
      <div css={styles.contentWrapper} ref={contentHeightRef}>
        <main id={MAIN_ELEMENT_ID} css={styles.main} ref={mainContentRef}>
          {contentEl}
        </main>

        <OnWhen {...panelSizes(panelViewSizes)}>
          {!loading && panelContent
            ? cloneElement(panelContent, {
                navHeight,
                contentHeight,
                navHeaderVisible,
                showContentHeader,
                footerHeight,
                mainContentHeight,
                footerRef
              })
            : null}
        </OnWhen>
      </div>

      {footer ? (
        <>
          {showFooterSpacer ? <Spacer size={1} /> : null}
          <footer css={{ marginBottom: footerMarginBottom }} ref={footerRef}>
            {footer}
          </footer>
        </>
      ) : null}
    </>
  );

  return (
    <div css={styles.wrapper} {...injectTestId(testID)}>
      <header css={styles.stickyContainer}>
        {cloneElement(header, {
          navHeight,
          setNavHeight,
          navHeaderVisible
        })}
      </header>
      {addSpacer ? <Spacer size={1 / 2} /> : null}
      {!loading && contentHeaderContent
        ? cloneElement(contentHeaderContent, { setContentHeaderHeight })
        : null}
      {coverImage}
      {fullPageLayoutContent}
    </div>
  );
};

PageLayout.propTypes = {
  header: PropTypes.node,
  coverImage: PropTypes.node,
  content: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  sidebar: PropTypes.node,
  footer: PropTypes.node,
  // Show LoadingSpinner in place of content while true
  loading: PropTypes.bool,
  // ErrorState properties
  error: PropTypes.shape(ErrorState.propTypes),
  // Set to true if page should not be visible to attendee
  permissionError: PropTypes.bool,
  // Requires a testID for each page
  testID: PropTypes.string.isRequired,
  // Used to override maxWidth for the page. Defaults to 1900
  maxContentWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // Used to disable application of maxWidth to the content body of the page
  disableContentMaxWidth: PropTypes.bool,
  // Passing error info into error state
  errorFields: PropTypes.shape({
    requestId: PropTypes.string,
    pageLoadId: PropTypes.string
  }),
  // Content that lives inside the page content header
  contentHeaderContent: PropTypes.object,
  // Content that lives inside the right side panel
  panelContent: PropTypes.node,
  // Engagment panel ref
  engagementRef: PropTypes.object,
  // The window sizes that the panel will display in, defaults to ['xl', 'l', 'm', 's', 'defaultSize']
  panelViewSizes: PropTypes.arrayOf(
    PropTypes.oneOf(['xl', 'l', 'm', 's', 'defaultSize'])
  ),
  // Remove padding to left and right of content? Defaults to false
  removeContentPadding: PropTypes.bool,
  // Show page content header?
  showContentHeader: PropTypes.bool,

  // Shows a space between page content and top of footer
  showFooterSpacer: PropTypes.bool,

  // DEPRECATED
  addSpacer: PropTypes.bool,
  stickyHeader: PropTypes.bool,
  hideHeader: PropTypes.bool
};

export default PageLayout;
